import { z } from "zod";

import type { GenderValEnum } from "shared/types";
import {
	AgeGroupEnum,
	BackendEntityType,
	EntitySoccerTeamRoles,
	GenderEnum,
	MatchResultEnum,
	UploadValidateTypes,
	baseCreateType,
	competitionGroupResolver,
	competitionResolver,
	leagueCompetitionResolver,
	paginationTokenResolver,
	ratingResolver
} from "shared/types";

import { AgeGenderLevelModel } from "./AgeGenderLevelModel";
import { coachResolver } from "./CoachModel";
import { logoResolver } from "./CreateTypes";
import { InvitationStatus } from "./Invitation";
import { playerResolver } from "./PlayerTypes";
import { structLevel } from "./SetupStructModel";
import { statsResolver } from "./StatModel";
import { userProfileResolver } from "./UserModel";

export const genericPlayerId = "GENERIC_PLAYER";

export enum PenaltyShootoutResult {
	goal = "GOAL",
	miss = "MISS"
}

export interface TeamModel {
	name: string;
	ageId: string;
	levelId: string;
	genderId: string;
	id?: string;
	suggestedLevel?: string;
	suggestedLevelId?: string;
}

export interface PredefinedTeamModel {
	name: string;
	behavior: AgeGroupEnum;
	age: number;
	level?: string;
	gender: GenderEnum;
	suggestedLevel?: string;
	suggestedLevelId?: string;
}

export const followedTeamsResolver = z.object({
	nextToken: paginationTokenResolver,
	previousToken: paginationTokenResolver,
	teams: baseCreateType.array()
});

export const standingResolver = z.object({
	position: z.number(),
	points: z.number(),
	matchesWon: z.number(),
	matchesLost: z.number(),
	matchesPlayed: z.number(),
	goalsScored: z.number(),
	goalsConceded: z.number(),
	goalsPerMatch: z.number(),
	starsReceived: z.number(),
	starsPerMatch: z.number(),
	yellowCards: z.number(),
	redCards: z.number(),
	rating: z.lazy(() => ratingResolver.optional()),
	conductPoints: z.number().optional(),
	goalDifferential: z.number().optional(),
	goalCappedDifferential: z.number().optional()
});

const playerResolverWithStats = statsResolver.extend({
	id: z.string(),
	name: z.string(),
	avatar: logoResolver.nullable()
});

export const penaltyShootoutResolver = z.object({
	id: z.string(),
	result: z.nativeEnum(PenaltyShootoutResult),
	shotBy: userProfileResolver.pick({ id: true, name: true, shortName: true, avatar: true }).nullable()
});

export type PenaltyShootoutModel = z.infer<typeof penaltyShootoutResolver>;

export const fullTeamResolver = AgeGenderLevelModel.extend({
	id: z.string(),
	name: z.string(),
	status: z.nativeEnum(InvitationStatus).optional(),
	club: baseCreateType.pick({ id: true, logo: true, name: true, location: true }).optional(),
	competitions: z.array(z.lazy(() => competitionResolver.pick({ id: true, name: true, logo: true }))).optional(),
	competitionCount: z.number().optional(),
	players: z.number().optional().or(z.array(playerResolverWithStats)),
	coach: z.string().optional(),
	groupId: z.string().optional(),
	standing: standingResolver.nullable().optional(),
	stats: statsResolver.nullable().optional(),
	rating: z.lazy(() => ratingResolver.optional()),
	score: z.number().optional().nullable(),
	penaltyShootouts: z.array(penaltyShootoutResolver).optional(),
	previousMatches: z
		.array(
			baseCreateType.pick({ id: true }).extend({
				result: z.lazy(() => z.nativeEnum(MatchResultEnum))
			})
		)
		.optional()
		.nullable(),
	nextMatches: z
		.array(
			baseCreateType.pick({ id: true }).extend({
				opponentTeam: z.lazy(() =>
					baseCreateType.pick({ id: true }).extend({
						name: z.string(),
						club: baseCreateType.pick({ id: true, logo: true, name: true }).optional()
					})
				)
			})
		)
		.optional()
		.nullable(),
	playerCount: z.number().optional(),
	coaches: z
		.array(
			z.lazy(() =>
				coachResolver
					.pick({
						id: true,
						name: true,
						shortName: true,
						avatar: true,
						gender: true,
						birthDate: true,
						email: true,
						firstName: true,
						lastName: true
					})
					.partial()
			)
		)
		.optional(),
	suggestedLevelId: z.string().optional().nullable(),
	suggestedLevel: structLevel.nullable().optional(),
	members: z
		.object({
			add: z.array(
				z.object({
					email: z.string(),
					role: z.nativeEnum(EntitySoccerTeamRoles)
				})
			),
			remove: z.array(
				z.object({
					userId: z.string(),
					role: z.nativeEnum(EntitySoccerTeamRoles)
				})
			)
		})
		.optional()
});

export const matchAttachmentInfo = baseCreateType
	.pick({
		id: true,
		location: true,
		name: true
	})
	.extend({
		homeTeam: fullTeamResolver.optional().nullable(),
		awayTeam: fullTeamResolver.optional().nullable(),
		startsAt: z.string().optional(),
		type: z.nativeEnum(BackendEntityType)
	});

export type MatchAttachmentInfoModel = z.infer<typeof matchAttachmentInfo>;

export type PlayerResolverWithStats = z.infer<typeof playerResolverWithStats>;

export type FullTeamModel = z.infer<typeof fullTeamResolver>;

export type StandingModel = z.infer<typeof standingResolver>;

export const teamFiltersResolver = z.object({
	ages: z.array(
		z.object({
			type: z.nativeEnum(AgeGroupEnum),
			value: z.number().or(z.null())
		})
	),
	genders: z.array(
		z.object({
			value: z.nativeEnum(GenderEnum)
		})
	),
	levels: z.array(
		z.object({
			value: z.number()
		})
	)
});

export type TeamFiltersModel = z.infer<typeof teamFiltersResolver>;

export const teamFiltersResolverWithId = z.object({
	id: z.string(),
	filters: teamFiltersResolver
});

export type TeamFiltersModelWithId = z.infer<typeof teamFiltersResolverWithId>;

export const teamPlayersResolver = z.lazy(() =>
	z.object({
		id: z.string().optional(),
		players: playerResolver.array().optional()
	})
);

export type TeamPlayersModel = z.infer<typeof teamPlayersResolver>;

export const teamCompetitionsResolver = z.lazy(() =>
	z.object({
		id: z.string().optional(),
		competitions: z.array(leagueCompetitionResolver)
	})
);
export type TeamCompetitionsModel = z.infer<typeof teamCompetitionsResolver>;

export const teamGroupsResolver = z.lazy(() =>
	competitionGroupResolver.pick({ id: true, logo: true, name: true, teams: true }).extend({
		competition: baseCreateType.pick({ id: true, name: true, logo: true }).extend({
			league: baseCreateType.pick({ id: true, name: true }).optional()
		}),
		division: baseCreateType.pick({ id: true, name: true, logo: true }).optional().nullable()
	})
);
export type TeamGroupsModel = z.infer<typeof teamGroupsResolver>;

export const teamCompetitionsStatsResolver = z.lazy(() =>
	z.object({
		id: z.string().optional(),
		competitions: z.array(competitionResolver.extend({ stats: statsResolver.nullable().optional() }))
	})
);
export type TeamCompetitionsStatsModel = z.infer<typeof teamCompetitionsStatsResolver>;

export interface CreateClubTeamModel {
	name: string;
	behavior: AgeGroupEnum;
	age: number;
	gender: GenderValEnum;
	level: string;
	primaryCoachName?: string | null;
	primaryCoachGender?: null | GenderValEnum;
	primaryCoachEmail?: string | null;
	secondaryCoachName?: string | null;
	secondaryCoachGender?: null | GenderValEnum;
	secondaryCoachEmail?: string | null;
	managerName?: string | null;
	managerEmail?: string | null;
}

export const bulkTeamUploadValidateResolver = z.object({
	id: z.string(),
	type: z.lazy(() => z.nativeEnum(UploadValidateTypes)),
	url: z.string()
});

export type BulkTeamUploadValidateModel = z.infer<typeof bulkTeamUploadValidateResolver>;
